import { VerifyPromoResponse } from "./"
import {
  pricingGreenColor,
  pricingOrangeColor,
  pricingPurpleColor,
} from "../../theme"
import {
  AddOnFeatureType,
  AdditionalContactIncentives,
  AdditionalCreditIncentiveType,
  AdditionalSubAccountIncentives,
  AdditionalUserIncentives,
  DiscountPriceApplicableOn,
  DiscountPriceBasedOn,
  DiscountPriceExpirationAfter,
  IncentiveType,
  IncentivesExpirationAfter,
  StandardPlanName,
  SubscriptionTerm,
  SystemAddOnType,
} from "../../utils/enumUtil"
import {
  addMonthsClamped,
  getPlanPriceForTerm,
  getTermMonths,
  isMultiMonthTerm,
  resolvePlanTerm,
} from "../../utils/subscriptionTermHelper"
import {
  getFormattedAmount,
  getKeyFromObjectValue,
  isUndefinedNullOrEmpty,
  useNumberWithCommas,
} from "../../utils/common"

export class PricingPlanResponseModel {
  plans
  promotionDetails
  constructor(data) {
    this.plans =
      data?.signUpPlans?.map((plan) => {
        return new PlanModel(plan)
      }) || []
    this.promotionDetails = new VerifyPromoResponse(
      data && data.validatePromotionDetails
    )
  }
}

//How many PAID add-on users a users incentive prices at its own pricePerUser: the incentive's "maximum
//users" (owner, 2026-09-18: "+100 users, max 50, $10" on a $500-per-user plan with 400 paid users charges
//50 at $10 and 350 at $500). -1 = no maximum set, every paid user gets the price. The free users
//(additionalUsers) are not counted against it. Same rule as the API and the Web model.
export const getIncentivePricedUsersCap = (users) =>
  users && users.isSetMaxUsers && users.numberOfMaxUsers > 0 ? users.numberOfMaxUsers : -1

export const getIncentivePricedUnits = (count, cap) =>
  count <= 0 ? 0 : cap < 0 ? count : Math.min(count, cap)

//One month of `count` units: the first units up to `cap` at the incentive price, the rest at the plan price.
export const priceUnitsWithIncentiveCap = (count, incentivePrice, planPrice, cap) => {
  const incentivePriced = getIncentivePricedUnits(count, cap)
  return incentivePriced * incentivePrice + (count - incentivePriced) * planPrice
}

export class LinkedAddOns {
  type
  price
  count
  pricePerAddOn
  availableInPlan
  freeAddOnCount
  //How many of `count` are charged pricePerAddOn; the rest pay the plan's list price. Equals `count`
  //unless a users incentive's "maximum users" caps its price (owner, 2026-09-18).
  incentivePricedCount
  constructor(data) {
    this.type = data?.type || ""
    this.price = data?.price || 0
    this.count = data?.count || 0
    this.pricePerAddOn = data?.pricePerAddOn || 0
    this.availableInPlan = data?.availableInPlan || false
    this.freeAddOnCount = data?.freeAddOnCount || 0
    this.incentivePricedCount = data?.incentivePricedCount ?? this.count
  }
}

export class IncentiveSummary {
  type
  description
  expirationDescription
  expirationMonths
  savingLabel
  savingAmount
  showInSavings

  constructor(data) {
    this.type = data?.type || ""
    this.description = data?.description || ""
    this.expirationDescription = data?.expirationDescription || ""
    this.expirationMonths = data?.expirationMonths || 0
    this.savingLabel = data?.savingLabel || ""
    this.savingAmount = data?.savingAmount || 0
    this.showInSavings = data?.showInSavings === true
  }
}

export class PlanModel {
  id
  name
  color
  backgroundColor
  monthlyPrice
  annualyPrice
  //Plan offers the 6-month term (Plan.AllowSemiAnnual)
  allowSemiAnnual
  //Admin-entered price for the whole 6-month term (Plan.SemiAnnualPrice); 0 when not allowed
  semiAnnualPrice
  isTrial
  addOnsAllowedInPlan
  featureAllowedInPlan
  creditPriceAddedToPlan
  minimumContactLimit
  maximumContactLimit
  minimumCreditLimit
  maximumCreditLimit
  maximumUsersAccountLimit
  minimumUsersAccountLimit
  minimumSubAccountsLimit
  maximumSubAccountsLimit
  reviewProfilesLimit
  creditPerEmail
  creditPerSMSRoute
  creditPerMMSRoute
  creditRollOverFrequencyLength
  creditRollOverFrequency
  isCurrentPlan
  amount
  subscriptionTerm
  isQuickStartAvailable
  isKnowledgeBaseAvailable
  isTrainingVideosAvailable
  isTrainingWebinarsAvailable
  isEmailSupportAvailable
  isChatSupportAvailable
  isUSPhoneSupportAvailable

  planPrice
  selectedContacts
  selectedCredits
  selectedSubAccounts
  selectedUsers
  selectedReviewProfiles
  purchaseAmount
  purchaseAmountWithPromotion
  addOns
  incentives

  constructor(data) {
    this.id = data?.id || ""
    this.name = data?.name || ""
    this.color =
      data && data.color && data.color.hexValue
        ? data.color.hexValue.trim().toUpperCase()
        : this.name.toLowerCase() === StandardPlanName.Essentials.toLowerCase()
          ? pricingOrangeColor
          : this.name.toLowerCase() === StandardPlanName.Premium.toLowerCase()
            ? pricingPurpleColor
            : pricingGreenColor
    this.backgroundColor = PlanModel.deserializeRGBABackgroundColor(data?.color)
    this.monthlyPrice = data?.monthlyPrice || 0
    this.annualyPrice = data?.annualyPrice || 0
    this.allowSemiAnnual = data?.allowSemiAnnual || false
    this.semiAnnualPrice = data?.semiAnnualPrice || 0
    this.isTrial = data?.isTrial || false
    this.addOnsAllowedInPlan = data?.addOnsAllowedInPlan || []
    this.featureAllowedInPlan = data?.featureAllowedInPlan || []
    this.creditPriceAddedToPlan = data?.creditPriceAddedToPlan || 0
    this.minimumContactLimit = data?.minimumContactLimit || 0
    this.maximumContactLimit = data?.maximumContactLimit || 0
    this.minimumCreditLimit = data?.minimumCreditLimit || 0
    this.maximumCreditLimit = data?.maximumCreditLimit || 0
    this.maximumUsersAccountLimit = data?.maximumUsersAccountLimit || 0
    this.minimumUsersAccountLimit = data?.minimumUsersAccountLimit || 0
    this.minimumSubAccountsLimit = data?.minimumSubAccountsLimit || 0
    this.maximumSubAccountsLimit = data?.maximumSubAccountsLimit || 0
    this.reviewProfilesLimit = data?.reviewProfilesLimit || 0
    this.creditPerEmail = data?.creditPerEmail || 0
    this.creditPerSMSRoute = data?.creditPerSMSRoute || 0
    this.creditPerMMSRoute = data?.creditPerMMSRoute || 0
    this.creditRollOverFrequencyLength =
      data?.creditRollOverFrequencyLength || 0
    this.creditRollOverFrequency = data?.creditRollOverFrequency || ""
    this.isCurrentPlan = data?.isCurrentPlan || false
    this.amount = data?.amount || 0
    this.isQuickStartAvailable = data?.isQuickStartAvailable || false
    this.isKnowledgeBaseAvailable = data?.isKnowledgeBaseAvailable || false
    this.isTrainingVideosAvailable = data?.isTrainingVideosAvailable || false
    this.isTrainingWebinarsAvailable =
      data?.isTrainingWebinarsAvailable || false
    this.isEmailSupportAvailable = data?.isEmailSupportAvailable || false
    this.isChatSupportAvailable = data?.isChatSupportAvailable || false
    this.isUSPhoneSupportAvailable = data?.isUSPhoneSupportAvailable || false

    this.planPrice = data?.planPrice || 0
    this.selectedContacts = data?.selectedContacts || this.minimumContactLimit
    this.selectedCredits = data?.selectedCredits || this.minimumCreditLimit
    this.selectedSubAccounts =
      data?.selectedSubAccounts || this.minimumSubAccountsLimit
    this.selectedUsers = data?.selectedUsers || this.minimumUsersAccountLimit
    this.selectedReviewProfiles = data?.selectedReviewProfiles || this.reviewProfilesLimit
    this.purchaseAmount = data?.purchaseAmount || this.amount
    this.purchaseAmountWithPromotion =
      data?.purchaseAmountWithPromotion || this.amount
    this.addOns = data?.addOns || []
    this.incentives = data?.incentives || []
  }

  static deserializeRGBABackgroundColor(colorData) {
    return colorData?.rgbValue
      ? `rgba(${colorData.rgbValue.r},${colorData.rgbValue.g},${colorData.rgbValue.b},${(colorData.rgbValue.a * 5) / 100
      })`
      : ""
  }

  /**
   * `subscriptionTerm` is the SubscriptionTerm the plan is priced for (Monthly / SemiAnnually /
   * Annually; anything else is billed as Monthly). It replaced the `isAnnualSubscription` boolean
   * everywhere in this model: the term decides the plan price (getPlanPriceForTerm) and the
   * number of months billed up front (getTermMonths: 1 / 6 / 12). A plan that does not offer the
   * selected term is quoted on Monthly (resolvePlanTerm), price AND months.
   */
  static deserializePlan(
    plan,
    subscriptionTerm,
    currentPlanAddOns,
    incentives
  ) {
    const term = resolvePlanTerm(plan, subscriptionTerm)
    const termMonths = getTermMonths(term)
    const contactLimits = this.getContactMinMaxLimitWithIncentives(
      incentives,
      plan
    )
    const creditLimits = this.getCreditMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const subAccountLimits = this.getSubMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const userLimits = this.getUserMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const reviewProfileLimits = this.getReviewProfilesMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    plan.subscriptionTerm = term
    plan.planPrice = getPlanPriceForTerm(plan, term)
    //Credits are granted up front for the whole term (plan credits x months), INCLUDING a credit
    //incentive's share - the base the slider and the API's grant start from, so the sidebar's
    //"Credits" and its "More Credits" count add up.
    plan.planCredits = PlanModel.getCreditMinMaxLimitWithIncentives(incentives, plan, term).minValue
    plan.addOns = []
    PlanModel.updateSelectedAddOns(plan, incentives, term)
    Object.keys(AddOnFeatureType).map((key) => {
      const addOn = currentPlanAddOns.find((x) => x.featureType === key)
      if (!addOn) return
      var months = termMonths
      if (addOn.featureCount > 0) {
        addOn.featureCount = addOn.featureCount || 0
        let addOnCount = addOn.featureCount
        switch (addOn.featureType) {
          case AddOnFeatureType.Contacts:
            const currentPlanSelectedContacts =
              contactLimits.minValue + addOnCount
            //const selectedContacts = contactLimits.minValue + addOnCount;
            if (
              contactLimits.minValue === -1 ||
              currentPlanSelectedContacts <= contactLimits.minValue
            ) {
              addOnCount = 0
            }
            // else if (selectedContacts > contactLimits.minValue) {
            //   addOnCount = contactLimits.maxValue - contactLimits.minValue;
            // } else {
            //   addOnCount = currentPlanSelectedContacts - contactLimits.minValue;
            // }
            plan.selectedContacts = contactLimits.minValue + addOnCount
            break
          case AddOnFeatureType.Credits:
            const currentPlanSelectedCredits =
              creditLimits.minValue + addOnCount
            const selectedCredits = creditLimits.minValue + addOnCount
            if (
              creditLimits.minValue === -1 ||
              currentPlanSelectedCredits <= creditLimits.minValue
            ) {
              addOnCount = 0
            }
            // else if (selectedCredits > plan.maximumCreditLimit) {
            //   addOnCount = plan.maximumCreditLimit - plan.minimumCreditLimit;
            // } else {
            //   addOnCount = currentPlanSelectedCredits - plan.minimumCreditLimit;
            // }
            plan.selectedCredits = creditLimits.minValue + addOnCount
            months = 1
            break
          case AddOnFeatureType.SubAccount:
            const currentPlanSelectedSubAccounts =
              subAccountLimits.minValue + addOnCount
            //const selectedSubAccounts = plan.minimumSubAccountsLimit + addOnCount;
            if (
              plan.minimumSubAccountsLimit === -1 ||
              currentPlanSelectedSubAccounts <= subAccountLimits.minValue
            ) {
              addOnCount = 0
            }
            // else if (selectedSubAccounts > plan.maximumSubAccountsLimit) {
            //   addOnCount = plan.maximumSubAccountsLimit - plan.minimumSubAccountsLimit;
            // } else {
            //   addOnCount = selectedSubAccounts - plan.minimumSubAccountsLimit;
            // }
            plan.selectedSubAccounts = subAccountLimits.minValue + addOnCount
            if (plan.selectedSubAccounts > subAccountLimits.maxValue) {
              plan.selectedSubAccounts = subAccountLimits.maxValue
            }
            break
          case AddOnFeatureType.User:
            const currentPlanSelectedUsers =
              userLimits.minValue + addOn.featureCount
            const selectedUsers =
              plan.minimumUsersAccountLimit + addOn.featureCount
            if (
              plan.minimumUsersAccountLimit === -1 ||
              currentPlanSelectedUsers <= userLimits.minValue
            ) {
              addOnCount = 0
            }
            // else if (selectedUsers > plan.maximumUsersAccountLimit) {
            //   addOnCount = plan.maximumUsersAccountLimit - plan.minimumUsersAccountLimit;
            // } else {
            //   addOnCount = selectedUsers - plan.minimumUsersAccountLimit;
            // }
            plan.selectedUsers = userLimits.minValue + addOnCount
            break
          case AddOnFeatureType.ReviewProfile:
            const currentPlanSelectedReviewProfiles =
              reviewProfileLimits.minValue + addOn.featureCount;
            if (
              plan.reviewProfilesLimit === -1 ||
              currentPlanSelectedReviewProfiles <= reviewProfileLimits.minValue
            ) {
              addOnCount = 0
            }
            plan.selectedReviewProfiles = reviewProfileLimits.minValue + addOnCount
            break
        }
        plan.addOns.push(
          new LinkedAddOns({
            type: addOn.featureType,
            count: addOnCount,
            pricePerAddOn: addOn.perUnitPrice,
            price: addOn.perUnitPrice * addOn.featureCount * months,
          })
        )
      }
    })
    plan = PlanModel.updatePlanSummaryWithIncentives(
      plan,
      incentives,
      term
    )
    return plan
  }

  static updatePlanSummaryWithIncentives(
    plan,
    incentives,
    subscriptionTerm
  ) {
    const term = resolvePlanTerm(plan, subscriptionTerm)
    const isMultiMonth = isMultiMonthTerm(term)
    plan.planPrice = getPlanPriceForTerm(plan, term)
    //Clamp the credit selection to the range the incentives give BEFORE pricing the add-ons. When a
    //promo was removed, the old selection (60,000 from a set-to-5,000 promo) was priced as a
    //57,600-credit add-on first and updateSelectedAddOns then re-derived the selection from that
    //add-on, so it never came back down (owner, 2026-09-14).
    const creditLimits = PlanModel.getCreditMinMaxLimitWithIncentives(incentives, plan, term)
    PlanModel.rebaseSelectionsOnFloors(
      plan,
      creditLimits,
      PlanModel.getUserMinMaxLimitWithIncentives(incentives, plan, term),
      PlanModel.getSubMinMaxLimitWithIncentives(incentives, plan, term),
      PlanModel.getReviewProfilesMinMaxLimitWithIncentives(incentives, plan, term)
    )
    PlanModel.syncSelectedCredits(plan, creditLimits)
    plan.addOns = PlanModel.calculateAddOns(
      plan,
      incentives,
      term
    )
    plan.incentives = PlanModel.deserializeIncentives(
      plan,
      incentives,
      term
    )
    const totalSavings =
      plan.incentives?.reduce(
        (a, b) =>
          b.type === IncentiveType.Credits ||
            b.type === IncentiveType.SubAccounts ||
            b.type === IncentiveType.Users ||
            b.type === IncentiveType.DiscountPrice
            ? a + b.savingAmount
            : a,
        0
      ) || 0
    const totalSavingsWithoutDiscount =
      plan.incentives?.reduce(
        (a, b) =>
          b.type === IncentiveType.Credits ||
            b.type === IncentiveType.SubAccounts ||
            b.type === IncentiveType.Users
            ? a + b.savingAmount
            : a,
        0
      ) || 0
    const totalAddOnPrice = PlanModel.totalAddOnPrice(plan.addOns)
    plan.purchaseAmount =
      plan.planPrice + totalAddOnPrice
    plan.purchaseAmountWithPromotion =
      plan.planPrice + totalAddOnPrice - totalSavings + totalSavingsWithoutDiscount
    const introductoryIncentive = incentives.find(
      (i) => IncentiveType[i.type] === IncentiveType.IntroductoryPrice
    )
    if (introductoryIncentive?.introductoryPrice) {
      let introPrice = introductoryIncentive.introductoryPrice.amount
      if (isMultiMonth) {
        const discountPriceWithIntro =
          plan.incentives?.reduce(
            (a, b) =>
              b.type === IncentiveType.IntroductoryPrice
                ? a + b.savingAmount
                : a,
            0
          ) || 0
        introPrice = plan.planPrice - discountPriceWithIntro
      }
      plan.purchaseAmountWithPromotion =
        introPrice + totalAddOnPrice
    }
    PlanModel.updateSelectedAddOns(plan, incentives, term)
    return plan
  }

  /**
   * What buying `term` saves against staying Monthly for the same months, priced the way the customer
   * is actually charged: the selected plan, its add-ons and the incentives on BOTH terms. The list-price
   * figure (`getTermSaving`) kept reading "Save $114" on a promo that made every term $0 (owner,
   * 2026-09-18), so the signup footer's term buttons use this one and print plain "Pay X" when nothing
   * is saved. A discount or introductory price that expires before the term's months are up is paid
   * Monthly for its months only, the list price for the rest, so a 3-month promo never makes a year of
   * Monthly look cheaper than it is. Negative = the term costs more (the button prints "Extra"). Same
   * rule as the Web app's PlanModel.getTermNetSaving.
   */
  static getTermNetSaving(plan, incentives, term) {
    const { monthlyCost, termCost } = PlanModel.getTermNetCosts(plan, incentives, term)
    return Math.round((monthlyCost - termCost) * 100) / 100
  }

  /**
   * The same saving as a whole percentage of what the months would cost Monthly ("Save 17%" on the
   * signup footer's single pay button, owner 2026-09-21); 0 for Monthly and when nothing is saved.
   */
  static getTermNetSavingPercentage(plan, incentives, term) {
    if (!isMultiMonthTerm(resolvePlanTerm(plan, term))) return 0
    const { monthlyCost, termCost } = PlanModel.getTermNetCosts(plan, incentives, term)
    const saving = monthlyCost - termCost
    return saving > 0 && monthlyCost > 0 ? Math.round((saving * 100) / monthlyCost) : 0
  }

  //What the term's months cost Monthly and what the term costs, both after the promo (see getTermNetSaving).
  //The PLAN only: add-ons are priced the same on every term (users / sub-accounts / review profiles per
  //month, extra credits a one-time lump sum), so they never change what the term saves, and in the
  //percentage they only diluted it ("Save 1%" on a $99 plan with $1,020 of users). Multiplying a lump-sum
  //credits add-on by the months also overstated the Monthly side.
  static getTermNetCosts(plan, incentives, term) {
    const paidTerm = resolvePlanTerm(plan, term)
    const months = getTermMonths(paidTerm)
    //updatePlanSummaryWithIncentives writes onto the plan it is given (selections, add-ons, floors):
    //price copies, never the plan on screen (the same copy-first rule as PricingMain.onSelectPlan).
    const priceOn = (onTerm) =>
      PlanModel.updatePlanSummaryWithIncentives({ ...plan }, incentives || [], onTerm)
    const planCost = (priced) =>
      priced.purchaseAmountWithPromotion - PlanModel.totalAddOnPrice(priced.addOns)
    const monthly = priceOn(SubscriptionTerm.Monthly)
    let monthlyCost = planCost(monthly) * months
    ;(monthly.incentives || []).forEach((summary) => {
      const isPriceIncentive =
        summary.type === IncentiveType.DiscountPrice ||
        summary.type === IncentiveType.IntroductoryPrice
      if (
        isPriceIncentive &&
        summary.savingAmount > 0 &&
        summary.expirationMonths > 0 &&
        summary.expirationMonths < months
      ) {
        monthlyCost += summary.savingAmount * (months - summary.expirationMonths)
      }
    })
    return { monthlyCost, termCost: planCost(priceOn(paidTerm)) }
  }

  //The credits number box (selectedInputCredits) is the user's draft while typing; whenever the model
  //moves the selection itself (promo applied or removed, term or plan changed) the box must follow,
  //or it keeps showing the value typed before the promo (owner, 2026-09-14). A draft that differs from
  //an UNCHANGED selection is left alone (the user is mid-typing).
  static setSelectedCredits(plan, value) {
    if (plan.selectedCredits === value) return
    plan.selectedCredits = value
    plan.selectedInputCredits = value
  }

  //Same clamp the slider applies on blur: over the ceiling -> back to the floor; under the floor -> floor.
  static syncSelectedCredits(plan, creditLimits) {
    let value = plan.selectedCredits
    if (value === -1 || value === undefined || value === null) return
    if (creditLimits.maxValue !== -1 && value > creditLimits.maxValue) {
      value = creditLimits.minValue
    } else if (value < creditLimits.minValue) {
      value = creditLimits.minValue
    }
    PlanModel.setSelectedCredits(plan, value)
  }

  //A selection is "floor + paid add-ons". The floor moves whenever a promo is applied or removed or
  //the term changes (a "+100 users" promo lifts the users floor from 5 to 105); the PAID count is what
  //must survive that move, not the raw number. Without this, removing the users promo (or switching the
  //term with a stale selection) kept 105 selected over a floor of 5 and priced 100 PAID users at
  //$21,000 / 6 months (owner, 2026-09-18), and a review-profile promo rejected on Monthly left its
  //5 free profiles as 5 paid ones. `plan.appliedFloors` records the floors the selection was last
  //based on. A plan without them (its first pass through the model) keeps the clamp-only behaviour
  //of the callers and only records its floors; the re-basing applies from the second pass on.
  static rebaseSelectionsOnFloors(plan, creditLimits, userLimits, subAccountLimits, reviewProfileLimits) {
    const previous = plan.appliedFloors
    if (previous) {
      const rebase = (selected, previousFloor, floor) => {
        if (selected === -1 || floor === -1 || previousFloor === -1) return selected
        if (selected === undefined || selected === null) return floor
        return floor + Math.max(0, selected - previousFloor)
      }
      PlanModel.setSelectedCredits(
        plan,
        rebase(plan.selectedCredits, previous.credits, creditLimits.minValue)
      )
      plan.selectedUsers = rebase(plan.selectedUsers, previous.users, userLimits.minValue)
      plan.selectedSubAccounts = rebase(
        plan.selectedSubAccounts,
        previous.subAccounts,
        subAccountLimits.minValue
      )
      plan.selectedReviewProfiles = rebase(
        plan.selectedReviewProfiles,
        previous.reviewProfiles,
        reviewProfileLimits.minValue
      )
    }
    plan.appliedFloors = {
      credits: creditLimits.minValue,
      users: userLimits.minValue,
      subAccounts: subAccountLimits.minValue,
      reviewProfiles: reviewProfileLimits.minValue,
    }
  }

  static updateSelectedAddOns(plan, incentives, subscriptionTerm) {
    const term = resolvePlanTerm(plan, subscriptionTerm)
    const creditLimits = PlanModel.getCreditMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const userLimits = PlanModel.getUserMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const subAccountLimits = PlanModel.getSubMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const reviewProfileLimits = PlanModel.getReviewProfilesMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )

    PlanModel.rebaseSelectionsOnFloors(
      plan,
      creditLimits,
      userLimits,
      subAccountLimits,
      reviewProfileLimits
    )

    const creditAddOns = plan.addOns?.find(
      (f) => f.type === AddOnFeatureType.Credits
    )
    const subAccountAddOn = plan.addOns?.find(
      (f) => f.type === AddOnFeatureType.SubAccount
    )
    const userAddOns = plan.addOns?.find(
      (f) => f.type === AddOnFeatureType.User
    )
    const reviewProfileAddOns = plan.addOns?.find(
      (f) => f.type === AddOnFeatureType.ReviewProfile
    )

    if (creditAddOns) {
      PlanModel.setSelectedCredits(plan, creditLimits.minValue + creditAddOns.count)
    } else {
      PlanModel.syncSelectedCredits(plan, creditLimits)
    }

    if (subAccountAddOn) {
      plan.selectedSubAccounts =
        subAccountLimits.minValue + subAccountAddOn.count
    } else if (
      subAccountLimits.maxValue !== -1 &&
      subAccountLimits.maxValue < plan.selectedSubAccounts
    ) {
      plan.selectedSubAccounts = subAccountLimits.minValue
    } else if (subAccountLimits.minValue > plan.selectedSubAccounts) {
      plan.selectedSubAccounts = subAccountLimits.minValue
    }

    if (userAddOns) {
      plan.selectedUsers = userLimits.minValue + userAddOns.count
    } else if (
      userLimits.maxValue !== -1 &&
      userLimits.maxValue < plan.selectedUsers
    ) {
      plan.selectedUsers = userLimits.minValue
    } else if (userLimits.minValue > plan.selectedUsers) {
      plan.selectedUsers = userLimits.minValue
    }
    if (reviewProfileAddOns) {
      plan.selectedReviewProfiles = reviewProfileLimits.minValue + reviewProfileAddOns.count
    } else if (
      reviewProfileLimits.maxValue !== -1 &&
      reviewProfileLimits.maxValue < plan.selectedReviewProfiles
    ) {
      plan.selectedReviewProfiles = reviewProfileLimits.minValue
    } else if (reviewProfileLimits.minValue > plan.selectedReviewProfiles) {
      plan.selectedReviewProfiles = reviewProfileLimits.minValue
    }
  }

  static calculateAddOns(plan, incentives, subscriptionTerm) {
    if (!plan) return []
    const term = resolvePlanTerm(plan, subscriptionTerm)
    //Months billed up front for the term (1 / 6 / 12). An incentive that expires part-way through
    //a multi-month term is blended: incentive price for `expirationMonths`, list price for the rest.
    const termMonths = getTermMonths(term)
    const isMultiMonth = isMultiMonthTerm(term)
    const userAddOnInfo =
      plan.addOnsAllowedInPlan?.find((a) => a.type === SystemAddOnType.Users) ||
      null
    const subAccountAddOnInfo =
      plan.addOnsAllowedInPlan?.find(
        (a) => a.type === SystemAddOnType.SubAccounts
      ) || null
    const reviewProfileAddOnInfo =
      plan.addOnsAllowedInPlan?.find((a) => a.type === SystemAddOnType.ReviewProfiles) ||
      null

    let pricePerUser = userAddOnInfo?.price || 0
    let pricePerSubAccount = subAccountAddOnInfo?.price || 0
    let pricePerReviewProfile = reviewProfileAddOnInfo?.price || 0
    let pricePerCredits = plan.creditPriceAddedToPlan || 0
    var months = termMonths
    var expirationMonths = termMonths

    const userIncentive = incentives.find(
      (i) => IncentiveType[i.type] === IncentiveType.Users
    )
    const subAccountIncentive = incentives.find(
      (i) => IncentiveType[i.type] === IncentiveType.SubAccounts
    )
    const creditIncentive = incentives.find(
      (i) => IncentiveType[i.type] === IncentiveType.Credits
    )
    const reviewProfileIncentive = incentives.find(
      (i) => IncentiveType[i.type] === IncentiveType.ReviewProfiles
    )
    const isSubAccountAddOnAvaialbleInPlan =
      subAccountAddOnInfo?.isAvailableInPlan === true
    const isUserAddOnAvaialbleInPlan = userAddOnInfo?.isAvailableInPlan === true
    const isReviewProfileAddOnAvaialbleInPlan = reviewProfileAddOnInfo?.isAvailableInPlan === true

    if (
      userIncentive?.users?.isSetUserPrice &&
      userAddOnInfo?.price > userIncentive.users.pricePerUser
    ) {
      pricePerUser = userIncentive.users.pricePerUser
    }

    if (
      subAccountIncentive?.subAccounts?.isSetSubAccountPrice &&
      subAccountAddOnInfo?.price >
      subAccountIncentive.subAccounts.pricePerSubAccount
    ) {
      pricePerSubAccount = subAccountIncentive.subAccounts.pricePerSubAccount
    }
    if (
      creditIncentive?.credits?.isSetCreditPrice &&
      pricePerCredits > creditIncentive.credits.pricePerCredit
    ) {
      pricePerCredits = creditIncentive.credits.pricePerCredit
    }

    if (
      reviewProfileIncentive?.reviewProfiles?.isSetReviewProfilePrice &&
      reviewProfileAddOnInfo?.price > reviewProfileIncentive.reviewProfiles.pricePerReviewProfile
    ) {
      pricePerReviewProfile = reviewProfileIncentive.reviewProfiles.pricePerReviewProfile
    }

    let newAddOns = []
    const creditLimits = this.getCreditMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const userLimits = this.getUserMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const subAccountLimit = this.getSubMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )
    const reviewProfileLimit = this.getReviewProfilesMinMaxLimitWithIncentives(
      incentives,
      plan,
      term
    )

    if (
      plan.selectedCredits !== -1 &&
      plan.selectedCredits > creditLimits.minValue
    ) {
      //Extra credits are a LUMP SUM bought once at the unit price (the incentive price when it is
      //lower), never per month of the term (owner, 2026-09-09: 6,000 more credits at $0.10 is $600
      //on a semi-annual plan, not $3,600). Same rule as the Web plan screen and the API.
      var price = pricePerCredits * (plan.selectedCredits - creditLimits.minValue)
      newAddOns.push(
        new LinkedAddOns({
          type: AddOnFeatureType.Credits,
          count: plan.selectedCredits - creditLimits.minValue,
          pricePerAddOn: pricePerCredits,
          price: price,
          availableInPlan: true,
          freeAddOnCount: creditLimits.freeAddOns,
        })
      )
    }
    if (
      plan.selectedSubAccounts !== -1 &&
      plan.selectedSubAccounts > subAccountLimit.minValue &&
      subAccountLimit.minValue !== -1
    ) {
      months = termMonths
      expirationMonths = 0
      if (
        subAccountIncentive?.subAccounts?.isExpirationSet &&
        subAccountIncentive?.subAccounts?.expireAfterValue > 0 &&
        isMultiMonth
      ) {
        expirationMonths = subAccountIncentive?.subAccounts.expireAfterValue
      }
      expirationMonths =
        expirationMonths == months
          ? 0
          : expirationMonths > termMonths
            ? termMonths
            : expirationMonths
      var price =
        pricePerSubAccount *
        (plan.selectedSubAccounts - subAccountLimit.minValue) *
        months
      if (isMultiMonth && expirationMonths > 0) {
        months = expirationMonths < months ? months - expirationMonths : months
        const addOnSubAccounts =
          plan.selectedSubAccounts - subAccountLimit.minValue
        const priceWithoutExpirationMonths =
          (subAccountAddOnInfo?.price || 0) * addOnSubAccounts * months
        const priceWithExpirationMonths =
          pricePerSubAccount * addOnSubAccounts * expirationMonths
        price = priceWithExpirationMonths + priceWithoutExpirationMonths
      }
      newAddOns.push(
        new LinkedAddOns({
          type: AddOnFeatureType.SubAccount,
          count: plan.selectedSubAccounts - subAccountLimit.minValue,
          pricePerAddOn: pricePerSubAccount,
          price: price,
          availableInPlan: isSubAccountAddOnAvaialbleInPlan,
          freeAddOnCount: subAccountLimit.freeAddOns,
        })
      )
    }
    if (
      plan.selectedUsers !== -1 &&
      plan.selectedUsers > userLimits.minValue &&
      userLimits.minValue !== -1
    ) {
      months = termMonths
      expirationMonths = 0
      if (
        userIncentive?.users?.isExpirationSet &&
        userIncentive?.users?.expireAfterValue > 0 &&
        isMultiMonth
      ) {
        expirationMonths = userIncentive?.users.expireAfterValue
      }
      expirationMonths =
        expirationMonths == months
          ? 0
          : expirationMonths > termMonths
            ? termMonths
            : expirationMonths
      const addOnUsers = plan.selectedUsers - userLimits.minValue
      const listPricePerUser = userAddOnInfo?.price || 0
      //The incentive's "maximum users" caps how many paid users get its price; the rest pay the plan price
      //(owner, 2026-09-18: 400 paid users, max 50 -> 50 x $10 + 350 x $500). Without a price incentive
      //pricePerUser IS the plan price and the split changes nothing. Same rule as the API and the Web model.
      const usersCap =
        pricePerUser < listPricePerUser ? getIncentivePricedUsersCap(userIncentive?.users) : -1
      const incentivePricedUsers = getIncentivePricedUnits(addOnUsers, usersCap)
      var price =
        priceUnitsWithIncentiveCap(addOnUsers, pricePerUser, listPricePerUser, usersCap) * months
      if (isMultiMonth && expirationMonths > 0) {
        months = expirationMonths < months ? months - expirationMonths : months
        const priceWithoutExpirationMonths = listPricePerUser * addOnUsers * months
        const priceWithExpirationMonths =
          priceUnitsWithIncentiveCap(addOnUsers, pricePerUser, listPricePerUser, usersCap) *
          expirationMonths
        price = priceWithExpirationMonths + priceWithoutExpirationMonths
      }
      newAddOns.push(
        new LinkedAddOns({
          type: AddOnFeatureType.User,
          count: addOnUsers,
          pricePerAddOn: pricePerUser,
          price: price,
          availableInPlan: isUserAddOnAvaialbleInPlan,
          freeAddOnCount: userLimits.freeAddOns,
          incentivePricedCount: incentivePricedUsers,
        })
      )
    }
    if (
      plan.selectedReviewProfiles !== -1 &&
      plan.selectedReviewProfiles > reviewProfileLimit.minValue &&
      reviewProfileLimit.minValue !== -1
    ) {
      months = termMonths
      expirationMonths = 0
      if (
        reviewProfileIncentive?.reviewProfiles?.isExpirationSet &&
        reviewProfileIncentive?.reviewProfiles?.expireAfterValue > 0 &&
        isMultiMonth
      ) {
        expirationMonths = reviewProfileIncentive?.reviewProfiles.expireAfterValue
      }
      expirationMonths =
        expirationMonths == months
          ? 0
          : expirationMonths > termMonths
            ? termMonths
            : expirationMonths
      var price =
        pricePerReviewProfile * (plan.selectedReviewProfiles - reviewProfileLimit.minValue) * months
      if (isMultiMonth && expirationMonths > 0) {
        months = expirationMonths < months ? months - expirationMonths : months
        const addOnReviewProfiles = plan.selectedReviewProfiles - reviewProfileLimit.minValue
        const priceWithoutExpirationMonths =
          (reviewProfileAddOnInfo?.price || 0) * addOnReviewProfiles * months
        const priceWithExpirationMonths =
          pricePerReviewProfile * addOnReviewProfiles * expirationMonths
        price = priceWithExpirationMonths + priceWithoutExpirationMonths
      }
      newAddOns.push(
        new LinkedAddOns({
          type: AddOnFeatureType.ReviewProfile,
          count: plan.selectedReviewProfiles - reviewProfileLimit.minValue,
          pricePerAddOn: pricePerReviewProfile,
          price: price,
          availableInPlan: isReviewProfileAddOnAvaialbleInPlan,
          freeAddOnCount: reviewProfileLimit.freeAddOns,
        })
      )
    }
    return newAddOns
  }

  static totalAddOnPrice(addOns) {
    return (
      addOns?.reduce((a, b) => a + (b.availableInPlan ? b.price : 0), 0) || 0
    )
  }

  static deserializeIncentives(plan, incentiveList, subscriptionTerm) {
    if (!plan || !incentiveList) return []
    const term = resolvePlanTerm(plan, subscriptionTerm)
    const termMonths = getTermMonths(term)
    const isMultiMonth = isMultiMonthTerm(term)
    let incentives = []
    Object.keys(IncentiveType).map((key) => {
      const incentive = incentiveList.find((x) => x.type === key)
      if (!incentive) return

      const totalSavingsAmount = incentives.reduce(
        (a, b) =>
          b.type === IncentiveType.Credits ||
            b.type === IncentiveType.SubAccounts ||
            b.type === IncentiveType.Users ||
            b.type === IncentiveType.ReviewProfiles
            ? a + b.savingAmount
            : a,
        0
      )

      let description = ""
      let expirationDescription = ""
      let savingLabel = ""
      let savingAmount = 0
      let showInSavings = true
      let incentiveTypeDetail = null
      let nowDate = new Date()
      let expirationDateTime = new Date()
      let expirationMonths = 0
      let pricePerAddOn = 0
      let additionalAddOnCount = 0
      const newAddOns = plan.addOns
      const creditAddOn = newAddOns.find(
        (x) => x.type === AddOnFeatureType.Credits
      )
      const subAccountAddOn = newAddOns.find(
        (x) => x.type === AddOnFeatureType.SubAccount
      )
      const userAddOn = newAddOns.find((x) => x.type === AddOnFeatureType.User)
      const contactAddOn = newAddOns.find(
        (x) => x.type === AddOnFeatureType.Contacts
      )
      let totalAddOnPrice = PlanModel.totalAddOnPrice(newAddOns)
      let isMultipleItemsInIncentive = false
      let planPrice = plan.planPrice
      const planColor = plan.color
      switch (IncentiveType[incentive.type]) {
        case IncentiveType.DiscountPrice:
          {
            let amountForApplicableDiscount = 0
            incentiveTypeDetail = incentive ? incentive.discountPrice : null
            //FIND DISCOUNT AMOUNT
            if (incentiveTypeDetail) {
              totalAddOnPrice = totalAddOnPrice - totalSavingsAmount
              //  For Eg. discount = 50% on entire purchase
              //  planPrice = $20 monthly;
              //  totalAddOnPrice = $0.6
              //IF A MULTI-MONTH TERM (ANNUAL / SEMI-ANNUAL) THEN GET 1 MONTH PLAN PRICE
              //NOTE: expirationMonths stays 0 unless the discount really expires; the signup footer
              //reads it as "this promo ends after N months" and a non-expiring discount used to be
              //stamped with the term's months here, so the footer printed a renewal at the list
              //price for a promo that never ends (owner, 2026-09-09).
              if (isMultiMonth) {
                planPrice = planPrice / termMonths
                totalAddOnPrice = totalAddOnPrice / termMonths
              }

              savingLabel = "Subscription Price"
              expirationDescription = "No Expiration"

              let discountPrice = 0
              const totalPrice = planPrice + totalAddOnPrice
              switch (
              DiscountPriceApplicableOn[
              incentiveTypeDetail.discountPriceApplicableOn
              ]
              ) {
                case DiscountPriceApplicableOn.Plan:
                  amountForApplicableDiscount = planPrice
                  break
                case DiscountPriceApplicableOn.AddOns:
                  amountForApplicableDiscount = totalAddOnPrice
                  break
                case DiscountPriceApplicableOn.EntirePurchase:
                  amountForApplicableDiscount = totalPrice
                  break
              }

              if (incentiveTypeDetail.isExpirationSet) {
                switch (incentiveTypeDetail.expireAfter) {
                  case DiscountPriceExpirationAfter.Months:
                    expirationMonths = incentiveTypeDetail.expireAfterValue
                    break
                  case DiscountPriceExpirationAfter.Payments:
                    {
                      const paymentsCount = incentiveTypeDetail.expireAfterValue
                      //ONE PAYMENT = ONE TERM (1 / 6 / 12 MONTHS); FOR MONTHLY MONTHS = PAYMENT'S COUNT
                      expirationMonths = paymentsCount * termMonths
                    }
                    break
                }

                expirationDateTime = addMonthsClamped(expirationDateTime, expirationMonths)
                expirationDescription = `${expirationDateTime.getMonth() + 1
                  }/${expirationDateTime.getDate()}/${expirationDateTime.getFullYear()}`
              }

              const discountPriceBasedOn =
                DiscountPriceBasedOn[incentiveTypeDetail.discountPriceBasedOn]
              //Months of the term the discount is applied over: the whole term when it never expires,
              //an expiry longer than the term is capped at the term (as the API does)
              const discountMonths = isMultiMonth
                ? expirationMonths > 0
                  ? Math.min(expirationMonths, termMonths)
                  : termMonths
                : 1
              var discountValue = isMultiMonth
                ? (incentiveTypeDetail.discountValue / termMonths) * discountMonths
                : incentiveTypeDetail.discountValue
              switch (discountPriceBasedOn) {
                case DiscountPriceBasedOn.Amount:
                  description = `$${incentiveTypeDetail.discountValue}`
                  //  discountPrice = 50
                  discountPrice = discountValue
                  break
                case DiscountPriceBasedOn.Percentage:
                  description = `${incentiveTypeDetail.discountValue}%`
                  //  discountPrice = (20 * 50) /100 = $10.3
                  discountPrice =
                    (amountForApplicableDiscount *
                      incentiveTypeDetail.discountValue) /
                    100
                  //A percentage with an expiry applies for the months it is valid, capped at the term, the
                  //same as the Amount shape above and as the API bills it: 20% off a $5,000/yr plan expiring
                  //after 3 months saves $250, not $1,000. discountMonths is the whole term when it never
                  //expires, so this is a no-op then; the term multiplier below makes it the term's saving.
                  if (isMultiMonth) {
                    discountPrice = (discountPrice * discountMonths) / termMonths
                  }
                  break
                case DiscountPriceBasedOn.SetAmount:
                  // if (plan.isCurrentPlan) {
                  description = `$${incentiveTypeDetail.discountValue}`
                  //discountPrice = amountForApplicableDiscount - incentiveTypeDetail.discountValue;
                  //}
                  //The plan is billed at the SET price for the months the incentive is valid and at the
                  //LIST price for the rest of the term (owner, 2026-09-10: $40 for 3 months on a $5,000/yr
                  //plan is $120 + 9 x $416.67 = $3,870, not $120). Per-month saving = (list - set) x months
                  //valid / term months; the whole-term multiplier below makes it the term's saving.
                  discountPrice =
                    amountForApplicableDiscount > 0
                      ? isMultiMonth
                        ? ((amountForApplicableDiscount - incentiveTypeDetail.discountValue) *
                            discountMonths) /
                          termMonths
                        : amountForApplicableDiscount - incentiveTypeDetail.discountValue
                      : 0
                  showInSavings = false
                  break
              }

              description = `${discountPriceBasedOn === DiscountPriceBasedOn.SetAmount
                ? "Set "
                : "Decrease By "
                }<span style="color:${planColor}; font-weight: 600">
                         ${description} </span>`
              switch (
              DiscountPriceApplicableOn[
              incentiveTypeDetail.discountPriceApplicableOn
              ]
              ) {
                case DiscountPriceApplicableOn.Plan:
                  description = `${description} Subscription Price | Plan`
                  savingAmount = discountPrice
                  break
                case DiscountPriceApplicableOn.AddOns:
                  description = `${description} Subscription Price | Add-On`
                  savingAmount = discountPrice
                  break
                case DiscountPriceApplicableOn.EntirePurchase:
                  description = `${description} Subscription Price | Entire Purchase`
                  savingAmount = discountPrice
                  break
              }
            }

            // if (isAnnualSubscription && savingAmount > 0) {
            //   amountForApplicableDiscount = amountForApplicableDiscount * 12;
            //   if (expirationMonths > 0 && expirationMonths <= 12) {
            //     savingAmount = savingAmount * expirationMonths;
            //   } else {
            //     savingAmount = savingAmount * 12;
            //   }
            // }

            //EG. $100 OFF FOR 8 MONTHS AND ANNUAL PRICE IS $275 SO
            //HERE FOR 8 MONTHS WE GOT $800  SO WE DO NOT HAVE TO PAY FOR THE $275
            if (savingAmount > amountForApplicableDiscount) {
              savingAmount = amountForApplicableDiscount
            }
          }
          break

        case IncentiveType.Credits:
          {
            incentiveTypeDetail = incentive ? incentive.credits : null
            if (incentiveTypeDetail) {
              expirationDescription = "No Expiration"
              let additionalCredits = 0
              let isSetCredits = false
              expirationMonths = termMonths
              if (incentiveTypeDetail.isExpirationSet) {
                switch (incentiveTypeDetail.expireAfter) {
                  case IncentivesExpirationAfter.Months:
                    {
                      expirationMonths = incentiveTypeDetail.expireAfterValue
                      expirationDateTime = addMonthsClamped(expirationDateTime, incentiveTypeDetail.expireAfterValue)
                    }
                    break
                  default:
                    expirationDateTime = null
                    break
                }

                if (expirationDateTime) {
                  expirationDescription = `${expirationDateTime.getMonth() + 1
                    }/${expirationDateTime.getDate()}/${expirationDateTime.getFullYear()}`
                }
              }

              if (incentiveTypeDetail.isSetAdditionalCredits) {
                switch (
                AdditionalCreditIncentiveType[
                incentiveTypeDetail.additionalCreditType
                ]
                ) {
                  case AdditionalCreditIncentiveType.IncreaseNumberOfCredits:
                    additionalCredits = incentiveTypeDetail.additionalCredits
                    isMultipleItemsInIncentive = true
                    description = `<span style="color:${planColor}; font-weight: 600">
                      ${useNumberWithCommas(additionalCredits, false)}</span> Extra Credit(s)`
                    break
                  case AdditionalCreditIncentiveType.IncreaseCreditsByPercentage:
                    //GET CREDIT'S PERCENTAGE COUNT
                    additionalCredits =
                      (plan.minimumCreditLimit *
                        incentiveTypeDetail.additionalCredits) /
                      100
                    isMultipleItemsInIncentive = true
                    description = `Increase <span style="color:${planColor}; font-weight: 600"> 
                      ${incentiveTypeDetail.additionalCredits}</span>% Credits`
                    break
                  case AdditionalCreditIncentiveType.SetCredits:
                    additionalCredits = incentiveTypeDetail.additionalCredits
                    isSetCredits = true
                    isMultipleItemsInIncentive = true
                    description = `<span style="color:${planColor}; font-weight: 600">
                      Set to </span>${useNumberWithCommas(additionalCredits, false)} Credit(s)`
                    break
                }
              }
              if (incentiveTypeDetail?.isSetCreditPrice) {
                description = `${description}${isMultipleItemsInIncentive ? `<br/>` : ""
                  } <span style="color:${planColor}; font-weight: 600"> $${useNumberWithCommas(
                    incentiveTypeDetail.pricePerCredit,
                    false
                  )} </span> per Credit`
                isMultipleItemsInIncentive = true
              }

              if (incentiveTypeDetail?.isSetMaxCredits) {
                description = `${description}${isMultipleItemsInIncentive ? `<br/>` : ""
                  } <span style="color:${planColor}; font-weight: 600"> ${useNumberWithCommas(
                    incentiveTypeDetail.numberOfMaxCredits,
                    false
                  )} </span> Max Credits`
                isMultipleItemsInIncentive = true
              }

              savingLabel = "Credits"
              pricePerAddOn = (creditAddOn && creditAddOn.pricePerAddOn) || 0
              const creditCount = (creditAddOn && creditAddOn.count) || 0
              additionalAddOnCount = creditCount
              // additionalCredits > creditCount ? creditCount : additionalCredits;

              //Priced for any credit incentive (the Web does the same); the old isSetCreditPrice
              //condition hid the row for an incentive that only adds credits.
              if (!isSetCredits) {
                const freeCreditsCount =
                  (creditAddOn && creditAddOn.freeAddOnCount) || 0
                savingAmount = freeCreditsCount * pricePerAddOn
                //savingAmount = additionalAddOnCount * pricePerAddOn;
              }

            }
          }
          break

        case IncentiveType.SubAccounts:
          {
            incentiveTypeDetail = isUndefinedNullOrEmpty(incentive)
              ? null
              : incentive.subAccounts
            const isSubAccountAddOnAvaialbleInPlan =
              plan.addOnsAllowedInPlan?.find(
                (a) => a.type === SystemAddOnType.SubAccounts
              )?.isAvailableInPlan === true
            if (incentiveTypeDetail) {
              expirationDescription = "No Expiration"

              let additionalSubAccounts = 0
              let isSetSubAccounts = false
              if (incentiveTypeDetail.isSetAdditionalSubAccounts) {
                switch (
                AdditionalSubAccountIncentives[
                incentiveTypeDetail.additionalSubAccountType
                ]
                ) {
                  case AdditionalSubAccountIncentives.IncreaseSubAccounts:
                    additionalSubAccounts =
                      incentiveTypeDetail.additionalSubAccounts
                    break
                  case AdditionalSubAccountIncentives.SetSubAccounts:
                    isSetSubAccounts = true
                    additionalSubAccounts =
                      incentiveTypeDetail.additionalSubAccounts
                    break
                }
              }
              savingLabel = "Sub-Accounts"
              pricePerAddOn =
                (subAccountAddOn && subAccountAddOn.pricePerAddOn) || 0
              // const subAccountCount = (subAccountAddOn && subAccountAddOn.count) || 0;
              // additionalAddOnCount = additionalSubAccounts > subAccountCount ? subAccountCount : additionalSubAccounts;
              // savingAmount = isSubAccountAddOnAvaialbleInPlan ? additionalAddOnCount * pricePerAddOn : 0;

              const freeSubAccountsCount =
                (subAccountAddOn && subAccountAddOn.freeAddOnCount) || 0
              savingAmount = freeSubAccountsCount * pricePerAddOn

              if (isSetSubAccounts) {
                isMultipleItemsInIncentive = true

                description = `Set to <span style="color:${planColor}; font-weight: 600">
                  ${additionalSubAccounts === -1
                    ? "Unlimited"
                    : `${useNumberWithCommas(additionalSubAccounts, false)}`
                  } </span> Sub-Account(s)`
              } else if (
                additionalSubAccounts > 0 ||
                additionalSubAccounts == -1
              ) {
                isMultipleItemsInIncentive = true
                description = `<span style="color:${planColor}; font-weight: 600">
                  ${additionalSubAccounts === -1
                    ? "Unlimited"
                    : useNumberWithCommas(additionalSubAccounts, false)
                  } </span>`
                description = `${description} ${additionalSubAccounts > 1 ? "Extra Sub-Account(s)" : "Sub-Account(s)"}`
              }

              if (incentiveTypeDetail.isSetMaxSubAccounts) {
                description = `${description} ${isMultipleItemsInIncentive ? `<br/>` : ""
                  }<span style="color:${planColor}; font-weight: 600">
                  ${useNumberWithCommas(incentiveTypeDetail.numberOfMaxSubAccounts, false)}</span> Max Sub-Account(s)`
                isMultipleItemsInIncentive = true
              }

              if (incentiveTypeDetail?.isSetSubAccountPrice) {
                description = `${description}${isMultipleItemsInIncentive ? `<br/>` : ""
                  } <span style="color:${planColor}; font-weight: 600"> $${useNumberWithCommas(
                    incentiveTypeDetail.pricePerSubAccount,
                    false
                  )} </span> per Sub-Account`
              }
            }
          }
          break

        case IncentiveType.Users:
          {
            incentiveTypeDetail = isUndefinedNullOrEmpty(incentive)
              ? null
              : incentive.users
            const isUserAddOnAvaialbleInPlan =
              plan.addOnsAllowedInPlan?.find(
                (a) => a.type === SystemAddOnType.Users
              )?.isAvailableInPlan === true
            let isSetUsers = false
            if (incentiveTypeDetail) {
              expirationDescription = "No Expiration"
              let additionalUsers = 0
              if (incentiveTypeDetail.isSetAdditionalUsers) {
                switch (
                AdditionalUserIncentives[
                incentiveTypeDetail.additionalUserType
                ]
                ) {
                  case AdditionalUserIncentives.IncreaseUser:
                    additionalUsers = incentiveTypeDetail.additionalUsers
                    break
                  case AdditionalUserIncentives.SetUsers:
                    isSetUsers = true
                    additionalUsers = incentiveTypeDetail.additionalUsers
                    break
                }
              }
              savingLabel = "Users"
              pricePerAddOn = (userAddOn && userAddOn.pricePerAddOn) || 0
              // const userCount = (userAddOn && userAddOn.count) || 0;
              // additionalAddOnCount = additionalUsers > userCount ? userCount : additionalUsers;
              // savingAmount = isUserAddOnAvaialbleInPlan ? additionalAddOnCount * pricePerAddOn : 0;

              const freeUsersCount =
                (userAddOn && userAddOn.freeAddOnCount) || 0
              savingAmount = freeUsersCount * pricePerAddOn

              if (isSetUsers) {
                isMultipleItemsInIncentive = true
                description = `Set to <span style="color:${planColor}; font-weight: 600">
                    ${useNumberWithCommas(additionalUsers, false)}</span> User(s)`
              } else if (additionalUsers > 0 || additionalUsers == -1) {
                isMultipleItemsInIncentive = true
                description = `<span style="color:${planColor}; font-weight: 600">
                  ${additionalUsers === -1 ? "Unlimited" : useNumberWithCommas(additionalUsers, false)} </span>`
                description = `${description} ${additionalUsers > 0 ? "Extra Users" : "User(s)"}`
              }

              if (incentiveTypeDetail.isSetMaxUsers) {
                description = `${description} ${isMultipleItemsInIncentive ? `<br/>` : ""
                  }<span style="color:${planColor}; font-weight: 600">
                  ${useNumberWithCommas(incentiveTypeDetail.numberOfMaxUsers, false)}</span> Max User(s)`
                isMultipleItemsInIncentive = true
              }

              if (incentiveTypeDetail) {
                if (incentiveTypeDetail.isSetUserPrice) {
                  description = `${description}${isMultipleItemsInIncentive ? `<br/>` : ""
                    } <span style="color:${planColor}; font-weight: 600">
                   $${useNumberWithCommas(incentiveTypeDetail.pricePerUser, false)}</span> per User`
                }
              }
            }
          }
          break

        case IncentiveType.ReviewProfiles:
          {
            incentiveTypeDetail = isUndefinedNullOrEmpty(incentive)
              ? null
              : incentive.reviewProfiles
            const isReviewProfileAddOnAvailableInPlan =
              plan.addOnsAllowedInPlan?.find(
                (a) => a.type === SystemAddOnType.ReviewProfiles
              )?.isAvailableInPlan === true
            if (incentiveTypeDetail) {
              expirationDescription = "No Expiration"

              let additionalReviewProfiles = 0
              if (incentiveTypeDetail.isSetAdditionalReviewProfiles) {
                //isSetAdditionalReviewProfiles is the increase (the response VM has no type field)
                additionalReviewProfiles = incentiveTypeDetail.additionalReviewProfiles
              }
              savingLabel = "Review Profiles"
              const reviewProfileAddOn = newAddOns.find(
                (x) => x.type === AddOnFeatureType.ReviewProfile
              )
              pricePerAddOn =
                (reviewProfileAddOn && reviewProfileAddOn.pricePerAddOn) || 0

              const freeReviewProfilesCount =
                (reviewProfileAddOn && reviewProfileAddOn.freeAddOnCount) || 0
              savingAmount = freeReviewProfilesCount * pricePerAddOn

              if (additionalReviewProfiles > 0 || additionalReviewProfiles === -1) {
                isMultipleItemsInIncentive = true
                description = `<span style="color:${planColor}; font-weight: 600">
                  ${additionalReviewProfiles === -1
                    ? "Unlimited"
                    : useNumberWithCommas(additionalReviewProfiles, false)
                  } </span>`
                description = `${description} ${additionalReviewProfiles > 1 ? "Extra Review Profile(s)" : "Review Profile(s)"}`
              }

              if (incentiveTypeDetail?.isSetReviewProfilePrice) {
                description = `${description}${isMultipleItemsInIncentive ? `<br/>` : ""
                  } <span style="color:${planColor}; font-weight: 600"> $${useNumberWithCommas(
                    incentiveTypeDetail.pricePerReviewProfile,
                    false
                  )} </span> per Review Profile`
              }
            }
          }
          break

        case IncentiveType.IntroductoryPrice:
          {
            incentiveTypeDetail = isUndefinedNullOrEmpty(incentive)
              ? null
              : incentive.introductoryPrice
            if (incentiveTypeDetail) {
              //APPLY INTRODUCTORY INCENTIVE FOR ONLY MONTHLY SUBSCRIPTION
              description = `<span style="color:${planColor}; font-weight: 600">$${incentiveTypeDetail.amount}</span> Introductory Price`
              expirationDescription = "No Expiration"
              expirationMonths = incentiveTypeDetail.expireAfterMonths
              let expirationDateTime = new Date()
              expirationDateTime = addMonthsClamped(expirationDateTime, incentiveTypeDetail.expireAfterMonths)
              savingLabel = "Introductory Price"
              savingAmount = planPrice - incentiveTypeDetail.amount
              if (isMultiMonth) {
                planPrice = planPrice / termMonths
                savingAmount = planPrice - incentiveTypeDetail.amount
                if (expirationMonths <= termMonths) {
                  savingAmount =
                    ((planPrice - incentiveTypeDetail.amount) *
                      expirationMonths) /
                    termMonths
                }
              }
              expirationDescription = `Expires after ${expirationMonths} ${expirationMonths > 1 ? "months" : "month"}`
            }
          }
          break
        case IncentiveType.CreditMultiplier:
          {
            incentiveTypeDetail = isUndefinedNullOrEmpty(incentive)
              ? null
              : incentive.creditMultiplier

            if (incentiveTypeDetail) {
              expirationDescription = "No Expiration"
              description = `Credit Multiplier`
              if (incentiveTypeDetail.isExpirationSet) {
                switch (incentiveTypeDetail.expireAfter) {
                  case IncentivesExpirationAfter.Months:
                    {
                      expirationMonths = incentiveTypeDetail.expireAfterValue
                      expirationDateTime = addMonthsClamped(expirationDateTime, expirationMonths)
                    }
                    break
                  case IncentivesExpirationAfter.Days:
                    {
                      expirationMonths = incentiveTypeDetail.expireAfterValue
                      expirationDateTime = new Date(
                        expirationDateTime.setDate(
                          nowDate.getDate() +
                          incentiveTypeDetail.expireAfterValue
                        )
                      )
                    }
                    break
                  default:
                    expirationDateTime = null
                    break
                }

                if (!isUndefinedNullOrEmpty(expirationDateTime)) {
                  expirationDescription = `${expirationDateTime.getMonth() + 1
                    }/${expirationDateTime.getDate()}/${expirationDateTime.getFullYear()}`
                }
              }
              if (incentiveTypeDetail.isSetForEmailRoute) {
                description += `<br/><span style="color:${planColor}; font-weight: 600">${incentiveTypeDetail.creditsPerEmail.toFixed(1)}</span> Credits per Email`
              }
              if (incentiveTypeDetail.isSetForSmsRoute) {
                description += `<br/><span style="color:${planColor}; font-weight: 600">${incentiveTypeDetail.creditsPerSms.toFixed(1)}</span> Credits per SMS`
              }
              if (incentiveTypeDetail.isSetForMMSRoute) {
                description += `<br/><span style="color:${planColor}; font-weight: 600">${incentiveTypeDetail.creditsPerMMS.toFixed(1)}</span> Credits per MMS`
              }
            }
          }
          break

        case IncentiveType.Contacts:
          {
            incentiveTypeDetail = isUndefinedNullOrEmpty(incentive)
              ? null
              : incentive.contacts
            if (incentiveTypeDetail) {
              expirationDescription = "No Expiration"
              let additionalContacts = 0
              let isSetContacts = false
              if (
                incentiveTypeDetail.isSetAdditionalContacts &&
                plan.minimumContactLimit !== -1
              ) {
                switch (
                AdditionalContactIncentives[
                incentiveTypeDetail.additionalContactType
                ]
                ) {
                  case AdditionalContactIncentives.IncreaseNumberOfContacts:
                    additionalContacts = incentiveTypeDetail.additionalContacts
                    break
                  case AdditionalContactIncentives.IncreaseContactsByPercentage:
                    additionalContacts = incentiveTypeDetail.additionalContacts
                    additionalContacts =
                      (plan.minimumContactLimit *
                        incentiveTypeDetail.additionalContacts) /
                      100
                    break
                  case AdditionalContactIncentives.SetContacts:
                    isSetContacts = true
                    additionalContacts = incentiveTypeDetail.additionalContacts
                    break
                  case AdditionalContactIncentives.UnlimitedContacts:
                    additionalContacts = -1
                    break
                }
              }
              savingLabel = "Contacts"
              pricePerAddOn = (contactAddOn && contactAddOn.pricePerAddOn) || 0
              const addOnContacts = (contactAddOn && contactAddOn.count) || 0
              additionalAddOnCount =
                additionalContacts > addOnContacts
                  ? addOnContacts
                  : additionalContacts
              savingAmount = additionalAddOnCount * pricePerAddOn

              if (isSetContacts) {
                isMultipleItemsInIncentive = true
                description = `<span style="color:${planColor}; font-weight: 600">
                 Set to </span>${useNumberWithCommas(additionalContacts, false)} Contacts`
              } else if (additionalContacts > 0 || additionalContacts == -1) {
                isMultipleItemsInIncentive = true

                description = `<span style="color:${planColor}; font-weight: 600">
                  ${additionalContacts === -1 ? "Unlimited" : useNumberWithCommas(additionalContacts, false)} </span>`

                description = `${description} ${additionalContacts > 0 ? "Extra Contact(s)" : "Contacts"}`
              }

              if (incentiveTypeDetail.isSetMaxContacts) {
                description = `${description} ${isMultipleItemsInIncentive ? `<br/>` : ""
                  } <span style="color:${planColor}; font-weight: 600">
                    ${useNumberWithCommas(incentiveTypeDetail.numberOfMaxContacts, false)}</span> Max Contact(s)`
                isMultipleItemsInIncentive = true
              }

              if (incentiveTypeDetail) {
                if (incentiveTypeDetail.isSetContactPrice) {
                  description = `${description}${isMultipleItemsInIncentive ? "<br/>" : ""
                    } <span style="color:${planColor}; font-weight: 600">
                    $${useNumberWithCommas(incentiveTypeDetail.pricePerContact, false)} </span> per Contact`
                }
              }
            }
          }
          break
      }

      //Credits are excluded: their free count is already the term's (getCreditMinMaxLimitWithIncentives).
      if (isMultiMonth && IncentiveType[incentive.type] !== IncentiveType.Credits) {
        savingAmount = savingAmount * termMonths
      }
      // if (isAnnualSubscription && savingAmount > 0) {
      //   amountForApplicableDiscount = amountForApplicableDiscount * 12;
      //   if (expirationMonths > 0 && expirationMonths <= 12) {
      //     savingAmount = savingAmount * expirationMonths;
      //   } else {
      //     savingAmount = savingAmount * 12;
      //   }
      // }
      savingAmount = parseFloat(getFormattedAmount(savingAmount))
      if (expirationDescription || description) {
        incentives.push(
          new IncentiveSummary({
            type: incentive.type,
            description: description,
            expirationDescription: expirationDescription,
            expirationMonths: expirationMonths,
            savingLabel: savingLabel,
            savingAmount: savingAmount,
            showInSavings: showInSavings,
          })
        )
      }
    })
    return incentives
  }

  static getSubMinMaxLimitWithIncentives(
    incentives,
    plan,
    subscriptionTerm
  ) {
    let minValue = plan.minimumSubAccountsLimit
    let maxValue = plan.maximumSubAccountsLimit

    let freeAddOns = 0

    const subAccountIncentive = incentives?.find(
      (i) => IncentiveType[i.type] === IncentiveType.SubAccounts
    )
    if (subAccountIncentive?.subAccounts) {
      if (subAccountIncentive.subAccounts.isSetMaxSubAccounts) {
        const incentiveMaxSubAccountLimit =
          subAccountIncentive.subAccounts.numberOfMaxSubAccounts
        if (plan.maximumSubAccountsLimit === -1) {
          maxValue = -1
        } else if (
          plan.maximumSubAccountsLimit < incentiveMaxSubAccountLimit ||
          incentiveMaxSubAccountLimit === -1
        ) {
          maxValue = incentiveMaxSubAccountLimit
        }
      }
      if (
        subAccountIncentive.subAccounts.isSetAdditionalSubAccounts &&
        plan.minimumSubAccountsLimit !== -1
      ) {
        const additonalSubAccountsValue =
          subAccountIncentive.subAccounts.additionalSubAccounts
        switch (
        AdditionalSubAccountIncentives[
        subAccountIncentive.subAccounts.additionalSubAccountType
        ]
        ) {
          case AdditionalSubAccountIncentives.SetSubAccounts:
            minValue = additonalSubAccountsValue
            break
          case AdditionalSubAccountIncentives.IncreaseSubAccounts:
            // minValue += additonalSubAccountsValue;
            freeAddOns = additonalSubAccountsValue
            break
        }
      }
    }
    if (freeAddOns > 0) {
      minValue += freeAddOns
    }
    if (minValue > maxValue && maxValue != -1) {
      maxValue = minValue
    }
    return {
      minValue: minValue,
      maxValue: maxValue,
      freeAddOns: freeAddOns,
    }
  }

  static getUserMinMaxLimitWithIncentives(
    incentives,
    plan,
    subscriptionTerm
  ) {
    let minValue = plan.minimumUsersAccountLimit
    let maxValue = plan.maximumUsersAccountLimit

    let freeAddOns = 0
    const userIncentive = incentives?.find(
      (i) => IncentiveType[i.type] === IncentiveType.Users
    )

    if (userIncentive?.users) {
      if (userIncentive.users.isSetMaxUsers) {
        //IF CURRENT LIMIT IS UNLIMITED
        if (plan.maximumUsersAccountLimit !== -1) {
          const incentiveMaxUserLimit = userIncentive.users.numberOfMaxUsers
          //IF INCENTIVE GIVES MORE LIMIT OR UNLIMITED
          if (
            plan.maximumUsersAccountLimit < incentiveMaxUserLimit ||
            incentiveMaxUserLimit == -1
          ) {
            maxValue = incentiveMaxUserLimit
          }
        }
      }
      //An unlimited-users plan stays unlimited (-1 + N would print a number); same guard as the Web.
      if (userIncentive.users.isSetAdditionalUsers && plan.minimumUsersAccountLimit !== -1) {
        switch (
        AdditionalUserIncentives[userIncentive.users.additionalUserType]
        ) {
          case AdditionalUserIncentives.IncreaseUser:
            const additionalUsers = userIncentive.users.additionalUsers
            freeAddOns = additionalUsers
            // minValue += additionalUsers;
            break
        }
      }
    }
    if (freeAddOns > 0) {
      minValue += freeAddOns
    }
    if (minValue > maxValue && maxValue != -1) {
      maxValue = minValue
    }
    return {
      minValue: minValue,
      maxValue: maxValue,
      freeAddOns: freeAddOns,
    }
  }
  static getReviewProfilesMinMaxLimitWithIncentives(
    incentives,
    plan,
    subscriptionTerm
  ) {
    let minValue = plan.reviewProfilesLimit
    let maxValue = 999;
    let freeAddOns = 0
    const reviewProfileIncentive = incentives?.find(
      (i) => IncentiveType[i.type] === IncentiveType.ReviewProfiles
    )
    if (reviewProfileIncentive?.reviewProfiles) {
      if (
        reviewProfileIncentive.reviewProfiles.isSetAdditionalReviewProfiles &&
        plan.reviewProfilesLimit !== -1
      ) {
        //The API's ReviewProfileSettingsViewModel carries no additionalReviewProfileType (its enum has
        //ONE member, IncreaseReviewProfile), so a switch on it never matched and the "+N review profiles"
        //part of a promo was never counted here while the API grants it. isSetAdditionalReviewProfiles IS
        //the increase.
        freeAddOns = reviewProfileIncentive.reviewProfiles.additionalReviewProfiles
      }
    }
    if (freeAddOns > 0) {
      minValue += freeAddOns
    }
    if (minValue > maxValue && maxValue != -1) {
      maxValue = minValue
    }
    return {
      minValue: minValue,
      maxValue: maxValue,
      freeAddOns: freeAddOns,
    }
  }

  static getCreditMinMaxLimitWithIncentives(
    incentives,
    plan,
    subscriptionTerm
  ) {
    const termMonths = getTermMonths(resolvePlanTerm(plan, subscriptionTerm))
    const isMultiMonth = termMonths > 1
    let minValue = plan.minimumCreditLimit
    let maxValue = plan.maximumCreditLimit
    let freeAddOns = 0
    const creditIncentive = incentives?.find(
      (i) => IncentiveType[i.type] === IncentiveType.Credits
    )

    if (creditIncentive?.credits) {
      if (
        creditIncentive.credits.isSetMaxCredits &&
        plan.maximumCreditLimit !== -1 &&
        plan.maximumCreditLimit < creditIncentive.credits.numberOfMaxCredits
      ) {
        maxValue = creditIncentive.credits.numberOfMaxCredits
      }
      if (creditIncentive.credits.isSetAdditionalCredits) {
        const additonalCreditsValue = creditIncentive.credits.additionalCredits
        switch (
        AdditionalCreditIncentiveType[
        creditIncentive.credits.additionalCreditType
        ]
        ) {
          case AdditionalCreditIncentiveType.SetCredits:
            //Set credits just set the value it can be less than the plan value
            minValue = additonalCreditsValue
            break
          case AdditionalCreditIncentiveType.IncreaseCreditsByPercentage:
            let additionalCreditsByPercentage =
              (plan.minimumCreditLimit * additonalCreditsValue) / 100
            // minValue += additionalCreditsByPercentage
            freeAddOns = additionalCreditsByPercentage
            break
          case AdditionalCreditIncentiveType.IncreaseNumberOfCredits:
            // minValue += additonalCreditsValue
            freeAddOns = additonalCreditsValue
            break
        }
      }
    }

    //A credit incentive is a per-month benefit granted for the WHOLE term, like the plan's own
    //credits: the API (GetFeatureMinMaxValues / AddAccountCredits) and the Web count it x term months
    //whether or not the incentive expires. The floor used to be capped by the expiry (+100 credits
    //expiring after 3 months on Annual: 6,300 here, 7,200 granted), so the signup sent a credit count
    //the API priced differently from this page. freeAddOns is returned term-scaled too, so the
    //SAVINGS row prices the credits the base actually carries.
    if (minValue != -1 && isMultiMonth) {
      minValue = minValue * termMonths
    }
    if (freeAddOns > 0) {
      freeAddOns = freeAddOns * termMonths
      minValue += freeAddOns
    }

    if (maxValue != -1 && isMultiMonth) {
      maxValue = maxValue * termMonths
    }
    //A "set credits to N" promo above the plan's ceiling (5,000 on a 200..500 plan) used to leave
    //the ceiling below the floor: the slider sat past its end and the box clamped back and forth.
    //The API never clamps a selection to the ceiling; the credits are simply fixed at the promo's.
    if (maxValue !== -1 && minValue !== -1 && maxValue < minValue) {
      maxValue = minValue
    }

    return {
      minValue: minValue,
      maxValue: maxValue,
      freeAddOns: freeAddOns,
    }
  }

  static getContactMinMaxLimitWithIncentives(incentives, plan) {
    //RULES: IF UPGRADE PLAN OFFERS BETTER THEN USE IT
    //Downgrade: do not use incentive here
    //Add-On: Use current incentive

    let minValue = plan.minimumContactLimit
    let maxValue = plan.maximumContactLimit
    const contactIncentive = incentives?.find(
      (i) => IncentiveType[i.type] === IncentiveType.Contacts
    )

    if (contactIncentive?.contacts) {
      const incentiveMaxContactLimit =
        contactIncentive.contacts?.numberOfMaxContacts

      //MAX CONTACTS
      if (
        contactIncentive.contacts.isSetMaxContacts &&
        plan.maximumContactLimit !== -1 &&
        (plan.maximumContactLimit < incentiveMaxContactLimit ||
          incentiveMaxContactLimit === -1)
      ) {
        maxValue = incentiveMaxContactLimit
      }

      //ADDITIONAL CONTACTS
      if (
        contactIncentive.contacts.isSetAdditionalContacts &&
        plan.minimumContactLimit !== -1
      ) {
        const additonalContactsValue =
          contactIncentive.contacts.additionalContacts
        switch (
        AdditionalContactIncentives[
        contactIncentive.contacts.additionalContactType
        ]
        ) {
          case AdditionalContactIncentives.SetContacts:
            maxValue = additonalContactsValue
            break
          case AdditionalContactIncentives.IncreaseNumberOfContacts:
            minValue += additonalContactsValue
            break
          case AdditionalContactIncentives.IncreaseContactsByPercentage:
            const additionalContactsByPercentage =
              (plan.minimumContactLimit * additonalContactsValue) / 100
            minValue += additionalContactsByPercentage
            break
          case AdditionalContactIncentives.UnlimitedContacts:
            minValue = additonalContactsValue
            break
        }
      }
    }

    return {
      minValue: minValue,
      maxValue: maxValue,
      minPlanValue: plan.minimumContactLimit,
    }
  }

  static filterApplicabledIncentiveByAddOns(selectedPlan, incentives) {
    if (!incentives || !selectedPlan) return []

    //ONLY KEEP ADD ON INCETIVES IF PLAN OFFERS.
    return incentives.filter((i) => {
      switch (IncentiveType[i.type]) {
        //"+N free" units are granted by the API (GetFeatureMinMaxValues raises the floor) whether or not the
        //plan SELLS that add-on, so such an incentive stays; only a PRICE-ONLY incentive for an add-on the
        //plan does not sell has nothing to apply to. Dropping every users incentive on a plan without the
        //Users add-on printed "the selected plan is better" and hid the free users (owner, 2026-09-23).
        case IncentiveType.SubAccounts:
          return (
            i.subAccounts?.isSetAdditionalSubAccounts === true ||
            selectedPlan?.addOnsAllowedInPlan?.find(
              (a) => a.type === SystemAddOnType.SubAccounts
            )?.isAvailableInPlan === true
          )
          break
        case IncentiveType.Users:
          return (
            i.users?.isSetAdditionalUsers === true ||
            selectedPlan?.addOnsAllowedInPlan?.find(
              (a) => a.type === SystemAddOnType.Users
            )?.isAvailableInPlan === true
          )
          break
        case IncentiveType.Training:
          return (
            selectedPlan?.addOnsAllowedInPlan?.find(
              (a) => a.type === SystemAddOnType.PersonalTrainingSession
            )?.isAvailableInPlan === true
          )
          break
        default:
          return true
          break
      }
    })
  }

  static filterIncentivesByRules(selectedPlan, incentives) {
    if (!incentives) {
      return []
    }
    return incentives.filter((i) => this.checkIfKeepIncentive(i, selectedPlan))
  }

  static checkIfKeepIncentive(incentive, plan) {
    let keep = true
    if (!plan) return keep
    switch (IncentiveType[incentive.type]) {
      case IncentiveType.Contacts:
        keep = PlanModel.checkIfKeepContactIncentive(plan, incentive)
        break
      case IncentiveType.Credits:
        keep = PlanModel.checkIfKeepCreditIncentive(plan, incentive)
        break
      case IncentiveType.SubAccounts:
        keep = PlanModel.checkIfKeepSubAccountIncentive(plan, incentive)
        break
      case IncentiveType.Users:
        keep = PlanModel.checkIfKeepUserIncentive(plan, incentive)
        break
      case IncentiveType.CreditMultiplier:
        keep = PlanModel.checkIfKeepCreditMultiplierIncentive(plan, incentive)
        break
    }
    return keep
  }

  static checkIfKeepCreditMultiplierIncentive(plan, creditMultiplierIncentive) {
    const planPerEmailCredits = plan.creditPerEmail
    const planPerSMSCredits = plan.creditPerSMSRoute
    const planPerMMSCredits = plan.creditPerMMSRoute
    let isPlanBetter = false
    if (creditMultiplierIncentive?.creditMultiplier) {
      if (creditMultiplierIncentive.creditMultiplier.isSetForEmailRoute) {
        if (
          planPerEmailCredits <
          creditMultiplierIncentive.creditMultiplier.creditsPerEmail
        ) {
          //plan better here
          isPlanBetter = true
        }
      }

      if (creditMultiplierIncentive.creditMultiplier.isSetForSmsRoute) {
        if (
          planPerSMSCredits <
          creditMultiplierIncentive.creditMultiplier.creditsPerSms
        ) {
          //plan better here
          isPlanBetter = true
        }
      }

      if (creditMultiplierIncentive.creditMultiplier.isSetForMMSRoute) {
        if (
          planPerMMSCredits <
          creditMultiplierIncentive.creditMultiplier.creditsPerMMS
        ) {
          //plan better here
          isPlanBetter = true
        }
      }
    } else {
      isPlanBetter = true
    }

    return isPlanBetter == false
  }

  static checkIfKeepCreditIncentive(plan, creditIncentive) {
    const minValue = plan.minimumCreditLimit

    let isPlanBetter = false
    if (creditIncentive?.credits) {
      //A "maximum credits" part LOWER than the plan's own ceiling is simply not applied (the
      //limit helper only ever raises the ceiling); it is not a reason to reject the whole promo,
      //which used to print "the selected plan is better" for a "set to 1,000 credits" promo on a
      //300-credit plan because the plan's maximum was above the incentive's 2,000 (owner,
      //2026-09-11). A plan with unlimited credits has nothing a credit incentive can improve.
      if (creditIncentive.credits.isSetMaxCredits && minValue == -1) {
        isPlanBetter = true
      }
      if (creditIncentive.credits.isSetAdditionalCredits) {
        const additonalCreditsValue = creditIncentive.credits.additionalCredits
        switch (
        AdditionalCreditIncentiveType[
        creditIncentive.credits.additionalCreditType
        ]
        ) {
          //A SET value is what the admin wants the account to hold, ABOVE or BELOW the plan's own
          //included credits: it is enforced, never dropped as "the plan is better" (owner, 2026-09-22;
          //the API floors credits at the set value x months on every path). Until then a "set to
          //1,000" promo on a 2,000-credit plan was hidden here while its id was still sent, so the API
          //floored at 6,000 on Semi-Annual and billed the 6,000 the page had left selected ($600 for a
          //$420 page). Only a plan with UNLIMITED credits has nothing to set.
          case AdditionalCreditIncentiveType.SetCredits:
            if (minValue == -1) isPlanBetter = true
            break
        }
      }
      if (creditIncentive.credits.isSetCreditPrice) {
        if (
          creditIncentive.credits.pricePerCredit > plan.creditPriceAddedToPlan
        ) {
          //plan better here
          isPlanBetter = true
        }
        //The extra-credit (recharge) price is NOT compared: the model never carried the plan's
        //creditPriceOneTimePurchase, so `pricePerExtraCredit > undefined` was always false and the check
        //had never rejected anything. Removed with the Web's copy (owner, 2026-09-18).
      }
    }

    return isPlanBetter == false
  }

  static checkIfKeepContactIncentive(plan, contactIncentive) {
    const minValue = plan.minimumContactLimit
    let isPlanBetter = false
    if (contactIncentive?.contacts) {
      //Same rule as users: a maximum the plan already beats is not applied, not a rejection.
      if (contactIncentive.contacts.isSetMaxContacts && minValue == -1) {
        isPlanBetter = true
      }
      //A "set contacts" incentive below the plan's own minimum is NOT rejected here: the check that meant
      //to (a switch on `contacts.additionalCreditType`, a field the contacts block does not have; the real
      //one is additionalContactType) matched nothing since it was written. Removed with the Web's copy
      //(owner, 2026-09-18).
    }
    return isPlanBetter == false
  }

  static checkIfKeepUserIncentive(plan, userIncentive) {
    const planUserAddOn = plan.addOnsAllowedInPlan?.find(
      (a) => a.type === SystemAddOnType.Users && a.isAvailableInPlan
    )

    let isPlanBetter = false
    if (userIncentive?.users) {
      //A "maximum users" part the plan already beats (unlimited, or a higher ceiling) is simply not
      //applied (the limit helper only ever raises the ceiling); it is not a reason to reject the
      //whole promo, which used to print "the selected plan is better" for "+100 users, maximum 500"
      //on a plan with unlimited users and hid the 100 free users (owner, 2026-09-18). Same rule as
      //the credits check. A plan with unlimited users included has nothing a user promo can improve.
      if (userIncentive.users.isSetMaxUsers && plan.minimumUsersAccountLimit == -1) {
        isPlanBetter = true
      }

      //A per-user price ABOVE the plan's own is simply not applied (the API's GetPerUnitPrice and both
      //pricing models take the lower of the two), and a users promo usually also adds free users, which
      //the API grants whatever the price says. It used to reject the whole promo ("the selected plan
      //is better"), hiding the free users (owner, 2026-09-23: "Users incentive is not working").
    }
    return isPlanBetter == false
  }

  static checkIfKeepSubAccountIncentive(plan, subAccountIncentive) {
    const planSubAccountAddOn = plan.addOnsAllowedInPlan?.find(
      (a) => a.type === SystemAddOnType.SubAccounts && a.isAvailableInPlan
    )

    let isPlanBetter = false
    if (subAccountIncentive?.subAccounts) {
      //Same rule as users: a maximum the plan already beats is not applied, not a rejection.
      if (
        subAccountIncentive.subAccounts.isSetMaxSubAccounts &&
        plan.minimumSubAccountsLimit == -1
      ) {
        isPlanBetter = true
      }

      //Same as users: a higher per-unit price is not applied, never a rejection (2026-09-23).
    }
    return isPlanBetter == false
  }
}
